/**
 * State Migration System for GlobalChat
 * Handles localStorage versioning, data schema changes, and backward compatibility
 */

export interface AppVersion {
  major: number;
  minor: number;
  patch: number;
}

export interface MigrationResult {
  success: boolean;
  migrated: string[];
  errors: string[];
  version: AppVersion;
}

export interface StorageSchema {
  version: AppVersion;
  keys: {
    [key: string]: {
      type: 'string' | 'object' | 'array' | 'boolean' | 'number';
      required?: boolean;
      deprecated?: boolean;
      migrateFrom?: string;
      validator?: (value: any) => boolean;
    };
  };
}

// Current app version
// Determine app version from build-time constant
const versionString = (typeof __APP_VERSION__ !== 'undefined' && __APP_VERSION__) ? __APP_VERSION__ : '1.2.0';
const [vMajor, vMinor, vPatch] = versionString.split('.').map((n) => Number(n) || 0);
export const CURRENT_VERSION: AppVersion = { major: vMajor, minor: vMinor, patch: vPatch };

// Storage schema definitions for different versions
export const STORAGE_SCHEMAS: Record<string, StorageSchema> = {
  '1.0.0': {
    version: { major: 1, minor: 0, patch: 0 },
    keys: {
      'vyzle-chat-language': { type: 'string', migrateFrom: 'traveler-chat-language' },
      'vyzle-chat-user-name': { type: 'string', migrateFrom: 'traveler-chat-user-name' },
      'vyzle-chat-onboarded': { type: 'string', migrateFrom: 'traveler-chat-onboarded' },
      'vyzle-chat-theme': { type: 'string', migrateFrom: 'traveler-chat-theme' },
    }
  },
  '1.1.0': {
    version: { major: 1, minor: 1, patch: 0 },
    keys: {
      'vyzle-chat-language': { type: 'string', migrateFrom: 'traveler-chat-language' },
      'vyzle-chat-user-name': { type: 'string', migrateFrom: 'traveler-chat-user-name' },
      'vyzle-chat-onboarded': { type: 'string', migrateFrom: 'traveler-chat-onboarded' },
      'vyzle-chat-theme': { type: 'string', migrateFrom: 'traveler-chat-theme' },
      'vyzle-chat-last-synced-user': { type: 'string', migrateFrom: 'traveler-chat-last-synced-user' },
      'vyzle-chat-last-sync-time': { type: 'string', migrateFrom: 'traveler-chat-last-sync-time' },
      'vyzle-chat-demo-user-id': { type: 'string', migrateFrom: 'traveler-chat-demo-user-id' },
    }
  },
  '1.2.0': {
    version: { major: 1, minor: 2, patch: 0 },
    keys: {
      // Core settings
      'vyzle-chat-language': { type: 'string', required: true, migrateFrom: 'traveler-chat-language' },
      'vyzle-chat-user-name': { type: 'string', migrateFrom: 'traveler-chat-user-name' },
      'vyzle-chat-onboarded': { type: 'string', migrateFrom: 'traveler-chat-onboarded' },
      'vyzle-chat-theme': { type: 'string', migrateFrom: 'traveler-chat-theme' },

      // Auth and sync
      'vyzle-chat-last-synced-user': { type: 'string', migrateFrom: 'traveler-chat-last-synced-user' },
      'vyzle-chat-last-sync-time': { type: 'string', migrateFrom: 'traveler-chat-last-sync-time' },
      'vyzle-chat-demo-user-id': { type: 'string', migrateFrom: 'traveler-chat-demo-user-id' },
      'vyzle-chat-auth-triggered': { type: 'string', migrateFrom: 'traveler-chat-auth-triggered' }, // sessionStorage

      // User preferences
      'vyzle-chat-user-preferences': { type: 'object', migrateFrom: 'traveler-chat-user-preferences' },
      'vyzle-chat-notification-settings': { type: 'object', migrateFrom: 'traveler-chat-notification-settings' },
      'vyzle-chat-privacy-settings': { type: 'object', migrateFrom: 'traveler-chat-privacy-settings' },

      // App state
      'vyzle-chat-app-version': { type: 'string', required: true, migrateFrom: 'traveler-chat-app-version' },
      'vyzle-chat-last-migration': { type: 'string', migrateFrom: 'traveler-chat-last-migration' },

      // Performance and caching
      'vyzle-chat-translation-cache': { type: 'object', migrateFrom: 'traveler-chat-translation-cache' },
      'vyzle-chat-room-cache': { type: 'object', migrateFrom: 'traveler-chat-room-cache' },

      // Legacy keys (deprecated)
      'traveler-chat-*': { type: 'string', deprecated: true },
      'user_language_*': { type: 'string', deprecated: true, migrateFrom: 'vyzle-chat-language' },
    }
  }
};

// Migration functions for specific version transitions
const MIGRATIONS: Record<string, (data: any) => any> = {
  '1.0.0->1.1.0': (data: any) => {
    // No significant changes, just version bump
    return data;
  },

  '1.1.0->1.2.0': (data: any) => {
    const migrated = { ...data };

    // Migrate user preferences to new structure
    const preferences: import('./types').UserPreferences = {
      autoTranslation: true,
      showOriginal: false,
      notificationsEnabled: true,
      soundEnabled: true,
      vibrationEnabled: true,
    };

    // Extract existing preferences from various sources
    if (data['vyzle-chat-language']) {
      preferences.primaryLanguage = data['vyzle-chat-language'];
    }

    migrated['vyzle-chat-user-preferences'] = JSON.stringify(preferences);

    // Migrate notification settings
    const notificationSettings = {
      enabled: true,
      sound: true,
      vibration: true,
      showPreviews: true,
      quietHours: null,
    };

    migrated['vyzle-chat-notification-settings'] = JSON.stringify(notificationSettings);

    // Migrate privacy settings
    const privacySettings = {
      anonymousMode: true,
      shareTypingStatus: true,
      shareOnlineStatus: true,
      allowInvites: true,
    };

    migrated['vyzle-chat-privacy-settings'] = JSON.stringify(privacySettings);

    // Initialize caches
    migrated['vyzle-chat-translation-cache'] = JSON.stringify({});
    migrated['vyzle-chat-room-cache'] = JSON.stringify({});

    return migrated;
  },
};

export class StateMigrationManager {
  private static instance: StateMigrationManager;

  public static getInstance(): StateMigrationManager {
    if (!StateMigrationManager.instance) {
      StateMigrationManager.instance = new StateMigrationManager();
    }
    return StateMigrationManager.instance;
  }

  /**
   * Get current app version from localStorage or package.json
   */
  private getCurrentStoredVersion(): AppVersion | null {
    try {
      const versionStr = localStorage.getItem('vyzle-chat-app-version');
      if (!versionStr) return null;

      const [major, minor, patch] = versionStr.split('.').map(Number);
      return { major, minor, patch };
    } catch (error) {
      console.warn('Failed to parse stored version:', error);
      return null;
    }
  }

  /**
   * Compare two versions
   */
  private compareVersions(v1: AppVersion, v2: AppVersion): number {
    if (v1.major !== v2.major) return v1.major - v2.major;
    if (v1.minor !== v2.minor) return v1.minor - v2.minor;
    return v1.patch - v2.patch;
  }

  /**
   * Get all localStorage data
   */
  private getAllStorageData(): Record<string, string> {
    const data: Record<string, string> = {};

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('vyzle-chat-')) {
        const value = localStorage.getItem(key);
        if (value !== null) {
          data[key] = value;
        }
      }
    }

    // Also check for legacy user-specific language keys
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('user_language_')) {
        const value = localStorage.getItem(key);
        if (value !== null) {
          data[key] = value;
        }
      }
    }

    return data;
  }

  /**
   * Clean up deprecated storage keys
   */
  private cleanupDeprecatedKeys(schema: StorageSchema): string[] {
    const cleaned: string[] = [];

    Object.entries(schema.keys).forEach(([pattern, config]) => {
      if (config.deprecated) {
        if (pattern.includes('*')) {
          // Handle wildcard patterns
          const prefix = pattern.replace('*', '');
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith(prefix)) {
              localStorage.removeItem(key);
              cleaned.push(key);
            }
          }
        } else {
          // Handle specific keys
          if (localStorage.getItem(pattern)) {
            localStorage.removeItem(pattern);
            cleaned.push(pattern);
          }
        }
      }
    });

    return cleaned;
  }

  /**
   * Validate storage data against schema
   */
  private validateData(data: Record<string, string>, schema: StorageSchema): string[] {
    const errors: string[] = [];

    Object.entries(schema.keys).forEach(([key, config]) => {
      if (config.required && !data[key]) {
        errors.push(`Missing required key: ${key}`);
        return;
      }

      if (data[key] && config.validator) {
        try {
          const value = config.type === 'object' ? JSON.parse(data[key]) : data[key];
          if (!config.validator(value)) {
            errors.push(`Invalid value for key: ${key}`);
          }
        } catch (error) {
          errors.push(`Failed to validate key ${key}: ${error}`);
        }
      }
    });

    return errors;
  }

  /**
   * Apply migration between two versions
   */
  private applyMigration(fromVersion: AppVersion, toVersion: AppVersion, data: Record<string, string>): Record<string, string> {
    const fromVersionStr = `${fromVersion.major}.${fromVersion.minor}.${fromVersion.patch}`;
    const toVersionStr = `${toVersion.major}.${toVersion.minor}.${toVersion.patch}`;
    const migrationKey = `${fromVersionStr}->${toVersionStr}`;

    const migrationFn = MIGRATIONS[migrationKey];
    if (migrationFn) {
      console.log(`Applying migration: ${migrationKey}`);
      return migrationFn(data);
    }

    console.log(`No specific migration found for ${migrationKey}, using default`);
    return data;
  }

  /**
   * Migrate user language from legacy keys
   */
  private migrateLegacyLanguageKeys(): string | null {
    let migratedLanguage: string | null = null;

    // Check for legacy user_language_* keys
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('user_language_')) {
        const value = localStorage.getItem(key);
        if (value && !migratedLanguage) {
          migratedLanguage = value;
          console.log(`Migrating language from legacy key: ${key} -> ${value}`);
        }
        // Clean up legacy key
        localStorage.removeItem(key);
      }
    }

    return migratedLanguage;
  }

  /**
   * Initialize default values for missing keys
   */
  private initializeDefaults(): void {
    const defaults = {
      'vyzle-chat-language': () => {
        // Try to migrate from legacy keys first
        const migrated = this.migrateLegacyLanguageKeys();
        if (migrated) return migrated;

        // Fall back to browser language
        return navigator.language.split('-')[0] || 'en';
      },
      'vyzle-chat-theme': () => 'system',
      'vyzle-chat-user-preferences': () => JSON.stringify({
        autoTranslation: true,
        showOriginal: false,
        notificationsEnabled: true,
        soundEnabled: true,
        vibrationEnabled: true,
      }),
      'vyzle-chat-notification-settings': () => JSON.stringify({
        enabled: true,
        sound: true,
        vibration: true,
        showPreviews: true,
        quietHours: null,
      }),
      'vyzle-chat-privacy-settings': () => JSON.stringify({
        anonymousMode: true,
        shareTypingStatus: true,
        shareOnlineStatus: true,
        allowInvites: true,
      }),
      'vyzle-chat-translation-cache': () => JSON.stringify({}),
      'vyzle-chat-room-cache': () => JSON.stringify({}),
    };

    Object.entries(defaults).forEach(([key, defaultFn]) => {
      if (!localStorage.getItem(key)) {
        const defaultValue = defaultFn();
        localStorage.setItem(key, defaultValue);
        console.log(`Initialized default value for: ${key}`);
      }
    });
  }

  /**
   * Perform complete migration process
   */
  public async migrate(): Promise<MigrationResult> {
    console.log('Starting state migration process...');

    const result: MigrationResult = {
      success: false,
      migrated: [],
      errors: [],
      version: CURRENT_VERSION,
    };

    try {
      const storedVersion = this.getCurrentStoredVersion();
      const currentData = this.getAllStorageData();

      console.log('Current stored version:', storedVersion);
      console.log('Target version:', CURRENT_VERSION);
      console.log('Storage data keys:', Object.keys(currentData));

      // If no version is stored, this is a fresh install or legacy version
      if (!storedVersion) {
        console.log('No version found - treating as fresh install or legacy migration');

        // Check if we have any legacy data
        const hasLegacyData = Object.keys(currentData).length > 0;

        if (hasLegacyData) {
          console.log('Found legacy data, migrating...');

          // Migrate from legacy (assume 1.0.0)
          const legacyVersion = { major: 1, minor: 0, patch: 0 };
          let migratedData = currentData;

          // Apply migrations step by step
          const versions = ['1.0.0', '1.1.0', '1.2.0'];
          for (let i = 0; i < versions.length - 1; i++) {
            const from = versions[i];
            const to = versions[i + 1];
            const fromVersion = STORAGE_SCHEMAS[from].version;
            const toVersion = STORAGE_SCHEMAS[to].version;

            migratedData = this.applyMigration(fromVersion, toVersion, migratedData);
            result.migrated.push(`${from} -> ${to}`);
          }

          // Write migrated data back to localStorage
          Object.entries(migratedData).forEach(([key, value]) => {
            localStorage.setItem(key, value);
          });
        }

        // Initialize defaults for missing values
        this.initializeDefaults();

        // Set current version
        localStorage.setItem('vyzle-chat-app-version', `${CURRENT_VERSION.major}.${CURRENT_VERSION.minor}.${CURRENT_VERSION.patch}`);
        localStorage.setItem('vyzle-chat-last-migration', new Date().toISOString());

        result.migrated.push('Initialized to current version');
      } else {
        // Check if migration is needed
        const comparison = this.compareVersions(storedVersion, CURRENT_VERSION);

        if (comparison < 0) {
          console.log('Migration needed from version:', storedVersion);

          // Apply incremental migrations
          let currentData = this.getAllStorageData();
          const versions = Object.keys(STORAGE_SCHEMAS).sort();

          for (let i = 0; i < versions.length - 1; i++) {
            const fromSchema = STORAGE_SCHEMAS[versions[i]];
            const toSchema = STORAGE_SCHEMAS[versions[i + 1]];

            if (this.compareVersions(storedVersion, fromSchema.version) >= 0 &&
              this.compareVersions(CURRENT_VERSION, toSchema.version) >= 0) {

              currentData = this.applyMigration(fromSchema.version, toSchema.version, currentData);
              result.migrated.push(`${versions[i]} -> ${versions[i + 1]}`);
            }
          }

          // Write migrated data back
          Object.entries(currentData).forEach(([key, value]) => {
            localStorage.setItem(key, value);
          });

          // Update version
          localStorage.setItem('vyzle-chat-app-version', `${CURRENT_VERSION.major}.${CURRENT_VERSION.minor}.${CURRENT_VERSION.patch}`);
          localStorage.setItem('vyzle-chat-last-migration', new Date().toISOString());

        } else if (comparison > 0) {
          console.warn('Stored version is newer than current version - possible downgrade');
          result.errors.push('Version downgrade detected');
        } else {
          console.log('No migration needed - versions match');
        }
      }

      // Clean up deprecated keys
      const currentSchema = STORAGE_SCHEMAS['1.2.0'];
      const cleaned = this.cleanupDeprecatedKeys(currentSchema);
      if (cleaned.length > 0) {
        result.migrated.push(`Cleaned deprecated keys: ${cleaned.join(', ')}`);
      }

      // Validate final state
      const finalData = this.getAllStorageData();
      const validationErrors = this.validateData(finalData, currentSchema);
      result.errors.push(...validationErrors);

      result.success = result.errors.length === 0;

      console.log('Migration completed:', result);
      return result;

    } catch (error) {
      console.error('Migration failed:', error);
      result.errors.push(`Migration error: ${error}`);
      result.success = false;
      return result;
    }
  }

  /**
   * Get user preferences with defaults
   */
  public getUserPreferences() {
    try {
      const stored = localStorage.getItem('vyzle-chat-user-preferences');
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.warn('Failed to parse user preferences:', error);
    }

    // Return defaults
    return {
      autoTranslation: true,
      showOriginal: false,
      notificationsEnabled: true,
      soundEnabled: true,
      vibrationEnabled: true,
    };
  }

  /**
   * Update user preferences
   */
  public updateUserPreferences(preferences: any) {
    try {
      const current = this.getUserPreferences();
      const updated = { ...current, ...preferences };
      localStorage.setItem('vyzle-chat-user-preferences', JSON.stringify(updated));
      return true;
    } catch (error) {
      console.error('Failed to update user preferences:', error);
      return false;
    }
  }

  /**
   * Clear all app data (for troubleshooting)
   */
  public clearAllData(): boolean {
    try {
      const keys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('vyzle-chat-') || key.startsWith('traveler-chat-') || key.startsWith('user_language_'))) {
          keys.push(key);
        }
      }

      keys.forEach(key => localStorage.removeItem(key));
      sessionStorage.clear();

      console.log('Cleared all app data:', keys);
      return true;
    } catch (error) {
      console.error('Failed to clear app data:', error);
      return false;
    }
  }

  /**
   * Export user data for backup
   */
  public exportData(): string {
    const data = this.getAllStorageData();
    return JSON.stringify({
      version: CURRENT_VERSION,
      timestamp: new Date().toISOString(),
      data,
    }, null, 2);
  }

  /**
   * Import user data from backup
   */
  public async importData(backupData: string): Promise<boolean> {
    try {
      const parsed = JSON.parse(backupData);

      if (!parsed.data || !parsed.version) {
        throw new Error('Invalid backup format');
      }

      // Clear existing data
      this.clearAllData();

      // Import data
      Object.entries(parsed.data).forEach(([key, value]) => {
        localStorage.setItem(key, value as string);
      });

      // Trigger migration to current version
      await this.migrate();

      return true;
    } catch (error) {
      console.error('Failed to import data:', error);
      return false;
    }
  }
}

// Singleton instance
export const stateMigration = StateMigrationManager.getInstance(); 